import { Component } from '@angular/core';
import { FieldType, FormlyFormBuilder, FormlyModule } from '@ngx-formly/core';
import { createRuleset, createRule } from '../utils';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-ruleset-type',
    templateUrl: './ruleset-type.component.html',
    styleUrls: ['./ruleset-type.component.scss'],
    imports: [
        MatButtonToggleGroup,
        MatButtonToggle,
        FormlyModule,
        MatButton,
        MatIcon,
    ],
})
export class RulesetTypeComponent extends FieldType {
  /** UI-only: collapsed state; not persisted in model */
  collapsed = false;

  constructor(private formlyBuilder: FormlyFormBuilder) {
    super();
  }

  /** Toggle this group's collapsed view */
  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  changeCondition(value: string): void {
    this.model.condition = value === 'ANY' ? 'OR' : 'AND';
    (this.options?.formState as any)?.workChanged?.();
  }

  /** Add a new empty rule to this ruleset */
  addRule(): void {
    this.model.rules.push(createRule());
    this.rebuild();
  }

  /** Add a new empty nested ruleset inside this ruleset */
  addRuleset(): void {
    this.model.rules.push(createRuleset());
    this.rebuild();
  }

  /** Duplicate this group (insert a deep copy right after it) */
  duplicateGroup(): void {
    const ctx = this.getParentArrayCtx();
    if (!ctx) return;

    const { parentArray, index } = ctx;
    const copy = JSON.parse(JSON.stringify(this.model));
    parentArray.splice(index + 1, 0, copy);
    this.rebuild();
  }

  /**
   * Remove this group from its parent's rules[].
   * If this is the root (no parent array), do nothing.
   */
  remove(): void {
    const ctx = this.getParentArrayCtx();
    if (!ctx) return; // likely the root group; don't remove
    const { parentArray, index } = ctx;

    parentArray.splice(index, 1);
    if ((this.options?.formState as any)?.workChanged && parentArray.length === 0) parentArray.push(createRule());
    this.rebuild();
  }

  /** Rebuild the formly tree so indexes/keys stay consistent */
  private rebuild(): void {
    (this.options as any)?.build?.();
    (this.options?.formState as any)?.workChanged?.();
  }

  /**
   * Locate the parent FieldArray and this group's index within it.
   * Structure is: [FieldArray 'rules'] -> [item with key = index] -> [this field]
   */
  private getParentArrayCtx():
    | { parentArray: any[]; index: number }
    | null {
    // item wrapper with key "i"
    const itemField = this.field?.parent;
    // the FieldArray ("rules") that owns all items
    const arrayField = itemField?.parent;

    if (!itemField || !arrayField) return null;

    const index = Number(itemField.key);
    const parentArray = Array.isArray(arrayField.model) ? arrayField.model : null;

    if (!parentArray || Number.isNaN(index)) return null;

    return { parentArray, index };
  }
}
